import { Box } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KitButton, KitCard, KitList, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { MobileActionItem, MobileListRow } from '../common/MobileCrud';
import { KitField, KitInput } from '../common/MobileKitExtras';
import { BarsChart, HBars, Kpi, KpiGrid, KitEmptyNote, PERIOD_OPTIONS, PeriodChips, detectPeriod, formatMoneyShort, periodRange, parseMoney } from '../common/MobileDashboardKit';
import { useAppBarAction } from '../layout/AppBarActions';

/**
 * Casca comum dos relatórios no shell mobile (`prototipos-mobile/relatorios/*.html`):
 * chips de período, ações da barra superior (Exportar / Filtros), folha de exportação
 * e folha de filtros (De / Até + campos extras da tela). Só apresentação: quem gera o
 * relatório, calcula os dados e exporta continua sendo a tela original.
 */

dayjs.extend(customParseFormat);

const SlidersIcon = protoIcon('sliders');
const DownloadIcon = protoIcon('download');

// Dispara a geração do relatório quando o usuário muda o período/aplica filtros.
// `fn` é lido via ref e roda no efeito (depois do render), então enxerga as datas já atualizadas.
// `skipInitial`: telas que já carregam sozinhas ao montar.
export function useMobileRegenerate(fn, { enabled, skipInitial = false } = {}) {
    const [tick, setTick] = useState(0);
    const fnRef = useRef(fn);
    fnRef.current = fn;
    useEffect(() => {
        if (!enabled) return;
        if (tick === 0 && skipInitial) return;
        fnRef.current();
    }, [tick, enabled, skipInitial]);
    return useCallback(() => setTick((t) => t + 1), []);
}

const toInput = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');
const fromInput = (text) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
};

/**
 * Props:
 * - startDate/endDate/setStartDate/setEndDate (dayjs) — omita `startDate` para telas sem período
 * - onGenerate() — pede nova geração (use `useMobileRegenerate`)
 * - onClear() — "Limpar" da folha de filtros (a tela volta aos padrões)
 * - extraFilters — campos extras da folha de filtros
 * - exportItems — [{ label, icon, onClick }] (vazio = sem botão Exportar)
 * - noFilters — sem botão Filtros (não usado hoje)
 */
export default function MobileReport({
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    onGenerate,
    onClear,
    extraFilters,
    exportItems = [],
    children,
}) {
    const [exportOpen, setExportOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const hasPeriod = Boolean(setStartDate);
    const activePeriod = hasPeriod ? detectPeriod(startDate, endDate) : null;

    const actions = [
        ...(exportItems.length > 0 ? [{ label: 'Exportar', icon: DownloadIcon, onClick: () => setExportOpen(true) }] : []),
        { label: 'Filtros', icon: SlidersIcon, onClick: () => setFiltersOpen(true) },
    ];
    useAppBarAction({ actions });

    const changePeriod = (id) => {
        const range = periodRange(id);
        setStartDate(range.start);
        setEndDate(range.end);
        onGenerate();
    };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {hasPeriod && (
                <PeriodChips options={PERIOD_OPTIONS} activeId={activePeriod} onChange={changePeriod} />
            )}
            {children}

            <MobileSheet open={exportOpen} onClose={() => setExportOpen(false)} title="Exportar">
                {exportItems.map((item) => (
                    <MobileActionItem
                        key={item.label}
                        icon={item.icon}
                        label={item.label}
                        onClick={() => {
                            setExportOpen(false);
                            item.onClick();
                        }}
                    />
                ))}
            </MobileSheet>

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={onClear}>Limpar</KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onGenerate();
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                {hasPeriod && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                        <KitField label="De">
                            <KitInput type="date" aria-label="Data inicial" value={toInput(startDate)} onChange={(e) => setStartDate(fromInput(e.target.value))} />
                        </KitField>
                        <KitField label="Até">
                            <KitInput type="date" aria-label="Data final" value={toInput(endDate)} onChange={(e) => setEndDate(fromInput(e.target.value))} />
                        </KitField>
                    </Box>
                )}
                {extraFilters}
            </MobileSheet>
        </Box>
    );
}

// `.list` com linhas de ranking: avatar numerado `.avatar.soft`, título/subtítulo e valor à direita (`.end .v`).
export function RankList({ rows }) {
    return (
        <KitList>
            {rows.map((row, index) => (
                <MobileListRow
                    key={`${row.title}-${index}`}
                    avatar={{ text: String(index + 1), tone: 'soft' }}
                    title={row.title}
                    sub={row.sub}
                    end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4, ...(row.endSx || {}) }}>{row.value}</Box>}
                />
            ))}
        </KitList>
    );
}

export { parseMoney };

// Valor dos KPIs: como `formatMoneyShort`, mas sem a casa decimal quando o valor em mil é redondo ("R$ 148 mil").
export function formatMoneyKpi(n) {
    const text = formatMoneyShort(n);
    return text.replace(/,0 mil$/, ' mil');
}

// Valor das linhas de ranking (`.end .v`): "R$ 2.480", "R$ 740,50" ou, a partir de 5 mil, "R$ 13,1 mil".
export function formatMoneyRank(n) {
    const value = Number.isFinite(n) ? n : 0;
    if (Math.abs(value) >= 5000) return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil`;
    const whole = Math.abs(value % 1) < 0.005;
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`;
}

// Agrupa linhas por dia (até 10 dias), semana (`S1..`, até ~2 meses) ou mês, somando `valueOf(row)`.
// Retorna [{ label, value }] para o `BarsChart`.
const MONTH_ABBR = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export function bucketByPeriod(rows, start, end, dateOf, valueOf) {
    if (!start || !end) return [];
    const s = start.startOf('day');
    const days = end.startOf('day').diff(s, 'day') + 1;
    let mode = 'month';
    if (days <= 10) mode = 'day';
    else if (days <= 62) mode = 'week';

    let buckets;
    let indexOf;
    if (mode === 'day') {
        buckets = Array.from({ length: days }, (_, i) => ({ label: s.add(i, 'day').format('DD'), value: 0 }));
        indexOf = (d) => d.startOf('day').diff(s, 'day');
    } else if (mode === 'week') {
        const count = Math.ceil(days / 7);
        buckets = Array.from({ length: count }, (_, i) => ({ label: `S${i + 1}`, value: 0 }));
        indexOf = (d) => Math.floor(d.startOf('day').diff(s, 'day') / 7);
    } else {
        const months = end.startOf('month').diff(s.startOf('month'), 'month') + 1;
        buckets = Array.from({ length: months }, (_, i) => ({ label: MONTH_ABBR[s.add(i, 'month').month()], value: 0 }));
        indexOf = (d) => d.startOf('month').diff(s.startOf('month'), 'month');
    }
    rows.forEach((row) => {
        const d = dateOf(row);
        if (!d || !d.isValid()) return;
        const idx = indexOf(d);
        if (idx >= 0 && idx < buckets.length) buckets[idx].value += valueOf(row);
    });
    return buckets;
}

const PackageIcon = protoIcon('package');

/**
 * Corpo padrão dos relatórios (`.kpis` + seções do protótipo).
 * - kpis: [{ label, icon, value, grad, delta }] — 4 itens, grade 2x2
 * - sections: [{ type: 'bars'|'hbars'|'rank'|'stock'|'kpis'|'node', title, items|rows, alt, empty }]
 *   - kpis: items [{ label, icon, value }] (grade 2 colunas extra, com título)
 *   - node: `node` livre (renderiza o próprio título; `title` só serve de chave)
 *   - bars: items [{ label, value }], alt [{ value }]
 *   - hbars: items [{ name, value, text }]
 *   - rank: rows [{ title, sub, value }]
 *   - stock: rows [{ title, sub, value }] (valor em `.stock-low`)
 * - ready: dados já carregados (seções só aparecem depois)
 */
export function MobileReportBody({ loading, ready, kpis, sections, firstKpis = false }) {
    return (
        <>
            <KpiGrid first={firstKpis}>
                {kpis.map((k) => (
                    <Kpi key={k.label} label={k.label} icon={k.icon} value={k.value} grad={k.grad} delta={k.delta} loading={loading && !ready} compact={!k.delta?.text} />
                ))}
            </KpiGrid>
            {ready && sections.map((section) => (
                <React.Fragment key={section.title}>
                    {section.title && section.type !== 'node' && <SecH title={section.title} />}
                    {section.type === 'node' && section.node}
                    {section.type === 'kpis' && (
                        <KpiGrid first>
                            {section.items.map((k) => (
                                <Kpi key={k.label} label={k.label} icon={k.icon} value={k.value} grad={k.grad} delta={k.delta} compact={!k.delta?.text} />
                            ))}
                        </KpiGrid>
                    )}
                    {section.type === 'bars' && (
                        <KitCard>
                            {section.items.length > 0 ? <BarsChart items={section.items} alt={section.alt} pairWidth={section.alt ? 12 : undefined} /> : <KitEmptyNote>{section.empty}</KitEmptyNote>}
                        </KitCard>
                    )}
                    {section.type === 'hbars' && (
                        <KitCard>
                            {section.items.length > 0 ? <HBars items={section.items} /> : <KitEmptyNote>{section.empty}</KitEmptyNote>}
                        </KitCard>
                    )}
                    {section.type === 'rank' && (section.rows.length > 0
                        ? <RankList rows={section.rows} />
                        : <KitCard><KitEmptyNote>{section.empty}</KitEmptyNote></KitCard>)}
                    {section.type === 'stock' && (section.rows.length > 0 ? (
                        <KitList>
                            {section.rows.map((row, index) => (
                                <MobileListRow
                                    key={`${row.title}-${index}`}
                                    icon={PackageIcon}
                                    title={row.title}
                                    sub={row.sub}
                                    end={<Box component="span" sx={{ color: '#b45309', fontWeight: 700 }}>{row.value}</Box>}
                                />
                            ))}
                        </KitList>
                    ) : <KitCard><KitEmptyNote>{section.empty}</KitEmptyNote></KitCard>)}
                </React.Fragment>
            ))}
        </>
    );
}
